import { useSyncExternalStore } from 'react';

// Who may make alterations (set from the workspace context).
let right = false;
const subs = new Set();
export function setAlterRight(v) { right = !!v; subs.forEach((f) => f()); }
export const useAlterRight = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => right);

export const CT_G = 0.2; // 1 carat = 0.2 g
export const MAT_KINDS = [['diamond', 'Diamond'], ['color_stone', 'Colour stone'], ['pearl', 'Pearl'], ['other', 'Other']];
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
export const lineCode = (m) => String(m.classification_raw || m.classification_schema || '').trim().toUpperCase();

// Non-gold lines of a piece as shown (index = position used by 'remove').
export const altLines = (raw) => (raw?.rm_consumed_snapshot || []).filter((m) => m.material_type !== 'gold')
  .map((m, i) => ({ i, code: lineCode(m), label: m.tag_display_name || m.classification_schema || lineCode(m), ct: Number(m.carats) || 0, pcs: Number(m.pieces) || 0 }));

function findLine(rm, a) {
  const rest = rm.map((m, k) => [m, k]).filter(([m]) => m.material_type !== 'gold').map(([, k]) => k);
  const code = String(a.code || '').trim().toUpperCase();
  if (a.i >= 0 && a.i < rest.length && lineCode(rm[rest[a.i]]) === code) return rest[a.i];
  const k = rest.find((x) => lineCode(rm[x]) === code);
  return k === undefined ? -1 : k;
}

// Same as the server (routes/tag_alters.py apply_alters): returns a new piece detail with the rows applied.
export function applyAlters(raw, rows) {
  if (!rows?.length) return raw;
  const rm = (raw.rm_consumed_snapshot || []).map((m) => ({ ...m }));
  let gross = Number(raw.gross_weight_grams) || 0;
  let net = Number(raw.net_weight_grams) || 0;
  const rates = { ...(raw.alter_rates || {}) };
  rows.forEach((a) => {
    if (a.kind === 'gold' || a.kind === 'gross') { const g = Number(a.grams) || 0; gross += g; net += g; return; }
    if (a.kind === 'add') {
      const ct = Number(a.carats) || 0; const code = String(a.code || '').trim().toUpperCase(); const name = String(a.name || '').trim();
      rm.push({ material_type: a.material_type || 'other', classification_schema: name || code, carats: ct, pieces: Number(a.pieces) || 0, issue_note: '', sieves: '', shape: '', size: '', classification_raw: code, tag_display_name: name, altered: 'add' });
      if (code && a.rate !== '' && a.rate !== undefined && a.rate !== null) rates[code] = Number(a.rate);
      gross += ct * CT_G; return;
    }
    if (a.kind === 'remove') {
      const k = findLine(rm, a); if (k < 0) return;
      const m = rm[k]; const ct = Math.min(Number(a.carats) || 0, Number(m.carats) || 0);
      m.carats = r3((Number(m.carats) || 0) - ct); m.pieces = Math.max(0, (Number(m.pieces) || 0) - (Number(a.pieces) || 0)); m.altered = 'remove';
      if (m.carats <= 0.0005) rm.splice(k, 1);
      gross -= ct * CT_G;
    }
  });
  return { ...raw, rm_consumed_snapshot: rm, gross_weight_grams: r3(gross), net_weight_grams: r3(net), alter_rates: rates, altered: true };
}

// Rows ready for the server (numbers, trimmed text).
export const cleanRows = (rows, allowTemp) => rows.map((a) => ({
  kind: a.kind, temp: !!(allowTemp && a.temp), grams: Number(a.grams) || 0, material_type: a.material_type || '', code: String(a.code || '').trim(), name: String(a.name || '').trim(),
  rate: a.rate === '' || a.rate === undefined ? null : Number(a.rate), carats: Number(a.carats) || 0, pieces: Math.round(Number(a.pieces) || 0), i: a.i ?? -1, note: String(a.note || '').trim(),
}));

// A row's problem (shown in red), or ''.
export function rowProblem(a) {
  if (a.kind === 'gold' || a.kind === 'gross') return Math.abs(Number(a.grams) || 0) < 0.0005 ? 'Enter the grams' : '';
  if (a.kind === 'add') return !String(a.code || '').trim() || !String(a.name || '').trim() ? 'Enter the classification and tag display name' : !(Number(a.carats) > 0) ? 'Enter the carats' : a.rate === '' || a.rate === undefined ? 'Enter the rate' : '';
  return !a.code ? 'Choose the line' : !(Number(a.carats) > 0) && !(Number(a.pieces) > 0) ? 'Enter carats or pieces removed' : '';
}

export function rowText(a) {
  const g = (n) => `${n > 0 ? '+' : ''}${Number(n).toFixed(3)} g`;
  if (a.kind === 'gold') return `Gold ${Number(a.grams) > 0 ? 'added' : 'removed'} ${g(a.grams)} (net and gross)`;
  if (a.kind === 'gross') return `Gross weight changed ${g(a.grams)} (net follows)`;
  if (a.kind === 'add') return `Added ${a.name || a.code} (${a.code}) · ${Number(a.carats).toFixed(2)} ct${a.pieces ? ` · ${a.pieces} pcs` : ''}${a.rate !== null && a.rate !== undefined ? ` · rate ${a.rate}` : ''}`;
  return `Removed from ${a.name || a.code} · ${Number(a.carats || 0).toFixed(2)} ct${a.pieces ? ` · ${a.pieces} pcs` : ''}`;
}
